import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../rootReducer'
import { EReaction } from '@/src/constants/reactions'

interface IEphemeralReaction {
  id: string
  type: keyof typeof EReaction
}

interface IParticipantReaction {
  isHandRaised: boolean
  ephemeralReactions: IEphemeralReaction[]
}

type TParticipantReaction = Record<string, IParticipantReaction>

export interface IReactionsState {
  currentRaiseHand: boolean
  participantsReactions: TParticipantReaction
}

export const initialReactionsState: IReactionsState = {
  currentRaiseHand: false,
  participantsReactions: {},
}

const reactionsAdapter = createEntityAdapter()

export const reactionsSlice = createSlice({
  name: 'reactions',
  initialState: reactionsAdapter.getInitialState(initialReactionsState),
  reducers: {
    setCurrentRaiseHand: (state, action: PayloadAction<boolean>) => {
      state.currentRaiseHand = action.payload
    },

    setParticipantHand: (
      state,
      action: PayloadAction<{ identity: string; isHandRaised: boolean }>,
    ) => {
      const { identity, isHandRaised } = action.payload
      if (!state.participantsReactions[identity]) {
        state.participantsReactions[identity] = {
          isHandRaised,
          ephemeralReactions: [],
        }
      } else {
        state.participantsReactions[identity].isHandRaised = isHandRaised
      }
    },

    addParticipantEphemeralReaction: (
      state,
      action: PayloadAction<{ identity: string; ephemeral: IEphemeralReaction }>,
    ) => {
      const { identity, ephemeral } = action.payload

      if (!state.participantsReactions[identity]) {
        state.participantsReactions[identity] = {
          isHandRaised: false,
          ephemeralReactions: [ephemeral],
        }

        return
      }

      if (
        !state.participantsReactions[identity].ephemeralReactions.find(
          (reaction) => reaction?.id === ephemeral?.id,
        )
      ) {
        state.participantsReactions[identity].ephemeralReactions.push(ephemeral)
        return
      }
    },

    removeParticipantEphemeralReaction: (
      state,
      action: PayloadAction<{ identity: string; reactionId: string }>,
    ) => {
      const { identity, reactionId } = action.payload
      if (!state.participantsReactions[identity]) return

      state.participantsReactions[identity].ephemeralReactions = state.participantsReactions[
        identity
      ].ephemeralReactions.filter((r) => r.id !== reactionId)
    },

    resetHandRaises: (state) => {
      state.currentRaiseHand = false
      for (const identity of Object.keys(state.participantsReactions)) {
        state.participantsReactions[identity].isHandRaised = false
      }
    },
  },
})

export const {
  setCurrentRaiseHand,
  setParticipantHand,
  addParticipantEphemeralReaction,
  removeParticipantEphemeralReaction,
  resetHandRaises,
} = reactionsSlice.actions

export const { selectAll: reactionsSelector } = reactionsAdapter.getSelectors(
  (state: RootState) => state[reactionsSlice.name],
)

export const selectReactionsState = (state: RootState) => state.reactions

export const selectCurrentRaiseHand = (state: RootState) => state.reactions.currentRaiseHand

export const selectParticipantsReactions = (state: RootState) =>
  state.reactions.participantsReactions
